import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { shelterAtom, shelterLoadingAtom, Shelter } from '../atoms/shelterAtom';
import { isAuthenticatedAtom, isInitializedAtom, invalidSoldToErrorAtom, preferredShipToAtom } from '../atoms/authAtom';
import api from '../services/api';

/**
 * Custom hook to read the shelter state.
 * Returns the current shelter data or null if not loaded/error.
 */
export const useShelterState = () => {
  const shelterData = useAtomValue(shelterAtom);
  const loadingState = useAtomValue(shelterLoadingAtom);

  return {
    shelter: shelterData,
    ...loadingState,
  };
};

// Module-level flag to prevent concurrent fetches across hook instances
let shelterFetchInFlight = false;

/**
 * Custom hook to provide actions related to shelter.
 * Provides functions to fetch shelter information from the backend API.
 */
export const useShelterActions = () => {
  const setShelter = useSetAtom(shelterAtom);
  const setLoadingState = useSetAtom(shelterLoadingAtom);
  const preferredShipTo = useAtomValue(preferredShipToAtom);

  // Helper function to set loading state
  const setLoading = useCallback(
    (isLoading: boolean, error: string | null = null) => {
      setLoadingState({ isLoading, error });
    },
    [setLoadingState]
  );

  // Function to fetch current shelter
  const getCurrentShelter = useCallback(async () => {
    // Synchronous guard — prevents duplicate calls during React render timing gaps
    if (shelterFetchInFlight) {
      console.debug('[useShelter] Fetch already in flight, skipping duplicate');
      return null;
    }
    shelterFetchInFlight = true;

    setLoading(true);
    try {
      const response = await api.get<Shelter>('/shelters/current');
      const shelterData = response.data;
      
      // Set preferred_ship_to from auth response if available and not empty
      if (preferredShipTo && preferredShipTo.trim() !== '') {
        shelterData.preferred_ship_to = preferredShipTo;
      } else {
        shelterData.preferred_ship_to = null;
      }
      
      setShelter(shelterData);
      setLoading(false);
      return shelterData;
    } catch (error) {
      console.error('[useShelter] Failed to fetch current shelter:', error);
      setLoading(false, error instanceof Error ? error.message : 'Failed to fetch shelter');
      return null;
    } finally {
      shelterFetchInFlight = false;
    }
  }, [setShelter, setLoading, preferredShipTo]);

  // Memoize actions to prevent dependency loops
  return useMemo(
    () => ({
      getCurrentShelter,
    }),
    [getCurrentShelter]
  );
};

/**
 * Combined hook for shelter operations
 */
export const useShelter = () => {
  const state = useShelterState();
  const actions = useShelterActions();
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const isInitialized = useAtomValue(isInitializedAtom);
  const invalidSoldToError = useAtomValue(invalidSoldToErrorAtom);
  const hasFetched = useRef(false);
  const retryCount = useRef(0);
  const MAX_RETRIES = 3;

  // Fetch shelter data on initial load, but only if authenticated, initialized, and no invalid sold_to error
  useEffect(() => {
    // Don't retry if there's an invalid sold_to error - the modal will be shown
    if (invalidSoldToError) {
      console.debug('[useShelter] Skipping shelter fetch - invalid sold_to error detected');
      retryCount.current = 0;
      return;
    }

    // Wait for auth to be fully initialized before fetching shelter data
    if (
      isAuthenticated &&
      isInitialized &&
      !state.shelter &&
      !state.isLoading &&
      !hasFetched.current
    ) {
      hasFetched.current = true;
      console.debug('[useShelter] Auth initialized and authenticated - fetching shelter data');
      actions.getCurrentShelter().then((result) => {
        // Allow limited retries if fetch failed
        if (!result) {
          retryCount.current++;
          if (retryCount.current >= MAX_RETRIES) {
            console.error(
              `[useShelter] Max retries (${MAX_RETRIES}) reached, stopping shelter fetch attempts`
            );
            // Don't reset hasFetched to prevent infinite retry loop
          } else {
            console.warn(
              `[useShelter] Retry ${retryCount.current}/${MAX_RETRIES} after failed fetch`
            );
            hasFetched.current = false;
          }
        } else {
          // Reset retry count on success
          retryCount.current = 0;
        }
      });
    } else if (!isAuthenticated) {
      hasFetched.current = false;
      retryCount.current = 0;
      console.debug('[useShelter] Skipping shelter fetch - user not authenticated');
    } else if (!isInitialized) {
      console.debug('[useShelter] Skipping shelter fetch - auth not yet initialized');
    }
  }, [isAuthenticated, isInitialized, state.shelter, state.isLoading, actions, invalidSoldToError]);

  return {
    ...state,
    ...actions,
  };
};
